import { useEffect, ReactNode, useRef } from 'react';
import {
  ChatBotFormAnswer,
  ChatFormItem,
  ChatItem,
  ChatItemForm,
  ChatItemLocationResults,
  ChatItemType,
} from 'app/services/chatBot';
import { TopicSource, TopicType } from 'app/copied/constants/common';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';

import { useVirtualAssistantStore } from '../store/virtualAssistantStore';
import useVirtualAssistantTracking from '../hooks/useVirtualAssistantTracking';

interface VirtualAssistantProviderProps {
  children: ReactNode;
}

export function VirtualAssistantProvider({ children }: VirtualAssistantProviderProps) {
  const trackingHooks = useVirtualAssistantTracking();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    useVirtualAssistantStore.setState({
      trackAnswerSubmitted: (
        formItem: ChatFormItem,
        answer: ChatBotFormAnswer,
        isConvAiActive: boolean,
        isConvAiChatItem: boolean
      ) => {
        trackingHooks.captureAnswerSubmitted(formItem, answer, isConvAiActive, isConvAiChatItem);
      },

      trackAnswerSubmissionErrors: (formItem: ChatFormItem) => {
        trackingHooks.captureAnswerSubmissionErrors(formItem);
      },

      trackFormDismissed: (formItem: ChatFormItem, isConvAiActive: boolean, isConvAiChatItem: boolean) => {
        trackingHooks.captureFormDismissed(formItem, isConvAiActive, isConvAiChatItem);
      },

      trackQuerySubmitted: (params: { query: string; isConvAiActive: boolean }) => {
        trackingHooks.captureQuerySubmitted(params);
      },

      trackMessageSent: (
        chatItem: ChatItem,
        isConvAiActive: boolean,
        isConvAiChatItem: boolean,
        additionalProps: object = {}
      ) => {
        trackingHooks.captureMessageSent(chatItem, isConvAiActive, isConvAiChatItem, additionalProps);
      },

      trackFormPresented: (
        chatItem: ChatItemForm,
        isConvAiActive: boolean,
        isConvAiChatItem: boolean,
        additionalProps: object = {}
      ) => {
        trackingHooks.captureFormPresented(chatItem, isConvAiActive, isConvAiChatItem, additionalProps);
      },

      trackLocationResultsDisplayed: (queryZip: string, locations: string[]) => {
        trackingHooks.captureLocationResultsDisplayed(queryZip, locations);
      },

      trackItemVisibility: (item: ChatItem) => {
        const store = useVirtualAssistantStore.getState();
        const isConvAiActive = store.isConvAiActive;
        const itemsTagInfo = store.itemsTagInfo;

        if (Object.hasOwn(itemsTagInfo, item.uuid)) {
          trackingHooks.captureMessageSent(item, isConvAiActive, item.aiResponse, itemsTagInfo[item.uuid]);
        } else if (item.type === ChatItemType.MESSAGE) {
          trackingHooks.captureMessageSent(item, isConvAiActive, item.aiResponse);
        } else if (item.type === ChatItemType.FORM) {
          trackingHooks.captureFormPresented(item as ChatItemForm, isConvAiActive, item.aiResponse);
        } else if (item.type === ChatItemType.LOCATION_RESULTS) {
          const locationItem = item as ChatItemLocationResults;
          trackingHooks.captureLocationResultsDisplayed(
            locationItem.origin_zip,
            locationItem.items.map(({ place_id }: { place_id: string }) => place_id)
          );
        }
      },

      trackKBEntryClicked: ({
        queryTyped,
        resultIndex,
        topicSource,
        topicType,
        topicTitle,
        kbEntryType,
        isConvAiChatItem,
        linkUrl,
      }: {
        queryTyped: string;
        resultIndex: number;
        topicSource?: TopicSource;
        topicType?: TopicType;
        topicTitle?: string;
        kbEntryType: PosthogEventName.RelatedResourcesClicked | PosthogEventName.TopicSourceClicked;
        isConvAiChatItem: boolean;
        linkUrl?: string;
      }) => {
        trackingHooks.captureKBEntryClicked({
          queryTyped,
          resultIndex,
          topicSource,
          topicType,
          topicTitle,
          kbEntryType,
          isConvAiChatItem,
          linkUrl,
        });
      },

      storeItemsTagInfo: (itemsTagInfo: {
        [key: string]: { tagId?: string; tagName?: string; posInResponse?: number };
      }) => {
        useVirtualAssistantStore.setState((state) => ({
          itemsTagInfo: { ...state.itemsTagInfo, ...itemsTagInfo },
        }));
      },
    });
  }, [trackingHooks]);

  return <>{children}</>;
}
